// @flow

import toPairs from 'lodash/toPairs';
import orderBy from 'lodash/orderBy';
import {matchedRegex, paramRequiresValue} from './general';
import {flagsRegex} from '../../terminal/command';
import type {Command, Param, Commands} from '../commands/resource';
import {TriggerTree} from '../commands/triggers';

type ParamTuple = [string, Param];
type CommandTuple = [string, Command];

// Removes non-repetable parameters which have been assigned.
function getFilteredParams(
  cur_params: Array<string>,
  params: Array<ParamTuple>,
): Array<string> {
  const refined_params = [];
  params.forEach(function(param, index) {
    if (
      (param[1].hasOwnProperty('repeatable') && param[1].repeatable) ||
      !(
        cur_params.includes(`--${param[0]}`) ||
        (param[1].hasOwnProperty('shorthand') &&
          cur_params.includes(`-${String(param[1].shorthand)}`))
      )
    ) {
      refined_params.push(param[0]);
    }
  });
  return refined_params;
}

// Returns a list of the correctly dashed parameters.
function getDashedParams(
  param_names: Array<string>,
  params: Array<ParamTuple>,
): Array<ParamTuple> {
  const dashed_params: Array<ParamTuple> = [];

  params.forEach(function(param, index) {
    if (param_names.includes(param[0])) {
      dashed_params.push([`--${param[0]}`, param[1]]);
    }
  });
  return dashed_params;
}

// Returns whether or not a parameter is compeleted.
function finishedParam(incompleted_param: string): boolean {
  let finished_tag = false;
  let in_quote = false;
  let finished_value = false;

  for (const char of incompleted_param) {
    if ((char === ' ' || char === '=') && !finished_tag) {
      finished_tag = true;
    } else if (char === '"') {
      in_quote = !in_quote;
    } else if (char === ' ' && finished_tag && !in_quote) {
      finished_value = true;
    }
  }
  return finished_value;
}

// Traverses the tree.
function traverseTree(
  command: Array<string>,
  tree: Commands,
): [Command | null, number | null] {
  let current_node = tree.root;
  let first_param = -1;
  for (let idx = 0; idx < command.length; idx++) {
    if (current_node.hasOwnProperty('run')) {
      first_param = idx;
      break;
    } else {
      current_node =
        current_node &&
        current_node.commands &&
        current_node.commands[command[idx]]
          ? current_node.commands[command[idx]]
          : null;
      if (!current_node) {
        return [null, null];
      }
    }
  }
  return [current_node, first_param];
}

// Get suggestions for parameters.
function getParamSuggestions(
  params: string,
  current_node: Command,
  root_node: Command,
): Array<ParamTuple> {
  let suggestions: Array<ParamTuple> = [];
  const completed_params = params.match(flagsRegex) ?? [];

  // Get the tags for parameters which have been used
  const completed_param_tags = [];
  let incomplete = false;
  completed_params.forEach(function(item) {
    const parts = flagsRegex.exec(item);
    flagsRegex.exec('');

    if (!parts || matchedRegex(parts, current_node)) {
      incomplete = true;
      return [];
    }

    const flag = parts.groups ? parts.groups.flag.trim() : '';
    completed_param_tags.push(flag);
  });

  const completed_params_str = completed_params.join(' ');
  let incompleted_param = params.slice(completed_params_str.length);

  if (
    incomplete ||
    (completed_params_str.length !== 0 &&
      incompleted_param.length === 0 &&
      params[params.length - 1] !== ' ' &&
      (!completed_param_tags[completed_param_tags.length - 1] ||
        paramRequiresValue(
          completed_param_tags[completed_param_tags.length - 1].trim(),
          current_node,
        )))
  ) {
    return [];
  }
  incompleted_param = incompleted_param.trimStart();

  // If the tag of the parameter is completed, do not give a suggestion.
  if (finishedParam(incompleted_param)) {
    suggestions = [];
  }
  // If the tag of the parameter is not complete, give a suggestion.
  else {
    const possible_params: Array<ParamTuple> = [];
    for (const [key, value] of toPairs(root_node.params ?? {})) {
      possible_params.push([key, value]);
    }
    for (const [key, value] of toPairs(current_node.params ?? {})) {
      possible_params.push([key, value]);
    }

    suggestions = matchingSubstring<Param>(
      incompleted_param,
      getDashedParams(
        getFilteredParams(completed_param_tags, possible_params),
        possible_params,
      ),
    );
  }
  return suggestions;
}

// Give the user suggestions for input.
export function giveSuggestion(
  command: Array<string>,
  tree: Commands,
): Array<ParamTuple> | Array<CommandTuple> | Array<[string, {}]> {
  if (
    command.length === 0 ||
    (command.length === 1 && command[0].length === 0)
  ) {
    const suggestions: Array<CommandTuple> = [];
    for (const [key, value] of toPairs(tree.root.commands)) {
      suggestions.push([key, value]);
    }
    return suggestions;
  }

  // Handle trigger case.
  if (
    command.length === 3 &&
    command[0] === 'stripe' &&
    command[1] === 'trigger'
  ) {
    const arg = command[2];
    const suggestions: Array<CommandTuple> = [];
    for (const key in TriggerTree) {
      if (
        TriggerTree.hasOwnProperty(key) &&
        key.length >= arg.length &&
        arg === key.slice(0, arg.length)
      ) {
        suggestions.push([key, {}]);
      }
    }
    return suggestions;
  }

  // Traverse the tree of commands.
  const traverse_output = traverseTree(command.slice(0, -1), tree);
  const current_node = traverse_output[0];
  let first_param = traverse_output[1];

  const empty_output: Array<ParamTuple> = [];
  if (!current_node || first_param === null) {
    return empty_output;
  }
  if (first_param < 0 && current_node && current_node.hasOwnProperty('run')) {
    first_param = command.length - 1;
  }

  let suggestions = [];

  const arg_count = current_node.args ? current_node.args.length : 0;

  // Handle the case where the user enter parameters.
  if (first_param > 0 && first_param + arg_count === command.length) {
    return empty_output;
  }
  // Handle the case where we suggest a parameter.
  else if (first_param > 0 && first_param + arg_count < command.length) {
    const params = command.slice(first_param + arg_count).join(' ');
    const cur_command = tree.root.commands
      ? tree.root.commands[command[0]]
      : {};

    suggestions = getParamSuggestions(params, current_node, cur_command);

    return orderBy(
      suggestions,
      [
        (option) =>
          option[1]?.type === 'param' && option[1].hasOwnProperty('required'),
        (option) => option[1]?.type === 'param' && !!option[1].required,
      ],
      ['desc', 'desc'],
    );
  }
  // Handle the case where we suggest a command.
  else {
    const all_possible: Array<CommandTuple> = [];
    if (current_node && current_node.hasOwnProperty('commands')) {
      for (const [key, value] of toPairs(current_node.commands)) {
        all_possible.push([key, value]);
      }
    }
    suggestions = matchingSubstring<Command>(
      command[command.length - 1],
      all_possible,
    );

    return suggestions;
  }
}

// Given a partial word and a list of options, return a list of potential matches
function matchingSubstring<T>(
  partial: string,
  options: Array<[string, T]>,
): Array<[string, T]> {
  const normalized = partial.toLowerCase();

  const refined_options = options.filter(
    (option) =>
      option[0].length >= normalized.length &&
      option[0].slice(0, normalized.length) === normalized,
  );
  return refined_options;
}
